import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ActiveWorkflowService } from '@shared/services/active-workflow.service';
import { NotificationService } from '@shared/services/notification.service';
import { IItemOperation, ProductionDataService } from '@shared/services/production-data.service';
import { ProductionTerminalService } from '@shared/services/production-terminal.service';
import { LocalStorageService } from '@shared/services/storage.service';
import { TerminalPrintService } from '@shared/services/terminal-print.service';
import { DataSourceTypes, IPrintConfiguration } from '@shared/services/terminalAdministration.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'app-terminal-menu',
	templateUrl: './terminal-menu.component.html',
	styleUrls: ['./terminal-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TerminalMenuComponent implements OnInit {
	@Output() showNonReadyChange = new EventEmitter<boolean>();
	showNonReady: boolean = false;
	private destroy$: Subject<void> = new Subject<void>();
	private allConfigurations: IPrintConfiguration[] = [];
	public items: IPrintConfiguration[] = [];
	constructor(
		private notificationService: NotificationService,
		private dataService: ProductionDataService,
		private localStorageService: LocalStorageService,
		private productionTerminalService: ProductionTerminalService,
		private terminalPrintService: TerminalPrintService,
		private activeWorkFlowService: ActiveWorkflowService,
	) {}

	ngOnInit(): void {
		this.setShowNonReady(this.getShowHideNonReadyPreference());

		this.productionTerminalService
			.onTerminalChanged()
			.pipe(takeUntil(this.destroy$))
			.subscribe((terminal) => {
				this.setShowNonReady(this.getShowHideNonReadyPreference());
				this.allConfigurations = terminal?.printConfigurations ?? [];
				this.applyStationFilter();
			});

		this.dataService
			.onStationNumberChanged()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.applyStationFilter());
	}

	private applyStationFilter(): void {
		const stationNumber = this.dataService.stationNumber;
		this.items = stationNumber ? this.allConfigurations.filter((c) => c.stationNumber == null || c.stationNumber === stationNumber) : this.allConfigurations;
	}

	getShowHideNonReadyPreference(): boolean {
		const terminalId = this.productionTerminalService.terminalId;
		const key = `${terminalId}#show-non-ready`;
		return this.localStorageService.get(key) === 'true';
	}

	toggleShowHideNonReady(): void {
		const showNonReady = !this.showNonReady;
		const terminalId = this.productionTerminalService.terminalId;
		const key = `${terminalId}#show-non-ready`;
		this.localStorageService.set(key, showNonReady.toString());
		this.setShowNonReady(showNonReady);
	}

	private setShowNonReady(value: boolean): void {
		if (this.showNonReady === value) {
			return;
		}

		this.showNonReady = value;
		this.showNonReadyChange.emit(value);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private resolveCurrentSequenceNumber(currentItem: IItemOperation): number {
		const { activeAlias, activeSequenceNumber } = this.activeWorkFlowService;
		const items = currentItem?.items ?? [];
		const readySequenceNumber = items.find((item) => item.ready)?.sequenceNumber;
		const maxSequenceNumber = items.reduce((max, item) => Math.max(max, item.sequenceNumber), 1);
		return readySequenceNumber == null ? maxSequenceNumber : activeAlias === currentItem?.alias ? (activeSequenceNumber ?? readySequenceNumber) : readySequenceNumber;
	}

	private buildItemElement(): string | null {
		const { currentItem } = this.dataService;
		if (!currentItem) {
			return null;
		}
		return `${currentItem.alias}%${this.resolveCurrentSequenceNumber(currentItem)}`;
	}

	private buildGroupElement(): string | null {
		return this.dataService.currentGroup?.groupSortKey ?? null;
	}

	copyItemData(): void {
		this.copyData(false);
	}

	copyGroupData(): void {
		this.copyData(true);
	}

	private copyData(isGroup: boolean): void {
		const element = isGroup ? this.buildGroupElement() : this.buildItemElement();
		if (!element) {
			this.notificationService.notify(['mes.administration.terminal.menu.copy.error.header'], [isGroup ? 'mes.administration.terminal.menu.copy.error.content.no-group' : 'mes.administration.terminal.menu.copy.error.content.no-item'], 'error');
			return;
		}
		this.copyPrintData(isGroup ? DataSourceTypes.Group : DataSourceTypes.Item, element);
	}

	private copyPrintData(kind: DataSourceTypes, element: string): void {
		this.terminalPrintService
			.getPrintData(this.productionTerminalService.terminalId, kind, element)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => this.copyToClipboard(data),
				error: () => this.notificationService.notify(['mes.administration.terminal.menu.copy.error.header'], ['mes.administration.terminal.menu.copy.error.content.default'], 'error'),
			});
	}

	private copyToClipboard(data: unknown): void {
		if (!navigator.clipboard) {
			this.notificationService.notify(['mes.administration.terminal.menu.copy.error.header'], ['mes.administration.terminal.menu.copy.error.content.default'], 'error');
			return;
		}

		navigator.clipboard
			.writeText(JSON.stringify(data, null, 2))
			.then(() => {
				this.notificationService.notify(['mes.administration.terminal.menu.copy.success.header'], ['mes.administration.terminal.menu.copy.success.content'], 'success');
			})
			.catch(() => {
				this.notificationService.notify(['mes.administration.terminal.menu.copy.error.header'], ['mes.administration.terminal.menu.copy.error.content.default'], 'error');
			});
	}

	startPrint(config: IPrintConfiguration): void {
		const isGroup = config.dataSource === DataSourceTypes.Group;
		const element = isGroup ? this.buildGroupElement() : this.buildItemElement();
		if (!element) {
			this.notificationService.notify(['mes.administration.terminal.menu.print.error.header'], [isGroup ? 'mes.administration.terminal.menu.print.error.content.no-group' : 'mes.administration.terminal.menu.print.error.content.no-item'], 'error');
			return;
		}

		this.terminalPrintService
			.print(this.productionTerminalService.terminalId, this.dataService.stationNumber, element, config.name)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => this.notificationService.notify(['mes.administration.terminal.menu.print.success.header'], ['mes.administration.terminal.menu.print.success.content'], 'success'),
				error: (err: HttpErrorResponse) => this.notificationService.notify(['mes.administration.terminal.menu.print.error.header'], [err?.status === 400 ? 'mes.administration.terminal.menu.print.error.content.bad-request' : 'mes.administration.terminal.menu.print.error.content.default'], 'error'),
			});
	}
}
